import { assertNever } from "@@/shared/assert-never";
import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import { VersionMismatchError } from "@@/shared/errors/version-mismatch-event.error";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { parseFulfillmentDate } from "@@/shared/reports/settlement-notes/parse-fulfillment-date";
import { asArray } from "@@/utilities/as-array";
import type { OrderEvent } from "../../order-events/events/_order-event";
import type { OrderChange } from "../../order-events/events/order-changed-event/order-changes/_order-change";
import { isOrderCreatedEvent } from "../../order-events/events/order-created.event";

import { isBeforeNow } from "@@/shared/date-time/date-time";
import {
    isOrderItemAccommodation,
    isOrderItemBookable,
    isOrderItemBookableQuantity,
} from "@@/shared/orders/order-items/is-order-item";
import type {
    OrderItemAccommodation_V2,
    OrderItemBookable_V2,
} from "@@/shared/orders/order-items/order-item-v2";
import { anonymizedContactInfoFactory } from "../../../locations/contact-info";
import { orderStatusCancelledFactory } from "../../order-statuses/order-cancelled";
import { orderStatusConfirmedFactory } from "../../order-statuses/order-confirmed";
import { orderStatusFulfilledFactory } from "../../order-statuses/order-fulfilled";
import { orderStatusOutForDeliveryFactory } from "../../order-statuses/order-out-for-delivery";
import { orderStatusPendingConfirmationFactory } from "../../order-statuses/order-pending-confirmation";
import { orderStatusPendingPaymentConfirmationFactory } from "../../order-statuses/order-pending-payment-confirmation";
import { orderStatusReadyForPickUpFactory } from "../../order-statuses/order-ready-for-pickup";
import { orderStatusRejectedFactory } from "../../order-statuses/order-rejected";
import { firstEventMustBeAnOrderCreatedEventCheck } from "../first-event-must-be-an-order-created-event-check";
import { missingOrderEventCheck } from "../missing-order-event-check";
import { legacyOrderPaidPrice } from "../shared/legacy-order-paid-price";
import { miniOrderEventFactory } from "./order-event-mini";
import type { OrderProjection } from "./order.projection";

const checkOrderId = (order: OrderProjection, event: OrderEvent) => {
    if (order._id !== event.orderId) {
        throw new Error(
            `Order id mismatch, cannot apply event ${event._id}|${event.type}|${event.orderId} on order ${order._id}`,
        );
    }
};

const applyOrderChanges = (
    order: OrderProjection,
    changeEventVersion: number,
    changes: OrderChange[],
) => {
    const updatedOrder = changes.reduce(
        (order: OrderProjection, change: OrderChange) => {
            switch (change.type) {
                case "ORDER_ITEM_ADDED": {
                    const items = [...order.orderItems, change.data.orderItem];
                    const amountUnsettled: number = order.amountUnsettled;
                    const amountToAdd: number = change.price.amountIncludingVat;
                    return {
                        ...order,
                        orderItems: items,
                        totalPrice: sumPrices(items.map(i => i.price)),
                        amountUnsettled: amountUnsettled + amountToAdd,
                    };
                }
                case "ORDER_ITEM_REMOVED": {
                    const items = order.orderItems.filter(
                        oi => oi._id !== change.data.orderItem._id,
                    );
                    const amountUnsettled: number = order.amountUnsettled;
                    const amountToAdd: number = change.price.amountIncludingVat;
                    return {
                        ...order,
                        orderItems: items,
                        totalPrice: sumPrices(items.map(i => i.price)),
                        amountUnsettled: amountUnsettled + amountToAdd,
                    } as OrderProjection;
                }
                case "ORDER_ITEM_BOOKABLE_TIME_CHANGED": {
                    const items = order.orderItems.map(oi => {
                        if (
                            oi._id === change.data.orderItemId &&
                            isOrderItemBookable(oi)
                        ) {
                            const newOrderItem: OrderItemBookable_V2 = {
                                ...oi,
                                time: change.data.to,
                            };
                            return newOrderItem;
                        }
                        return oi;
                    });
                    const amountUnsettled: number = order.amountUnsettled;
                    const amountToAdd: number = change.price.amountIncludingVat;
                    return {
                        ...order,
                        orderItems: items,
                        amountUnsettled: amountUnsettled + amountToAdd,
                    };
                }
                case "ORDER_ITEM_PRODUCT_CHANGED": {
                    //This is a information event, no changes to order that is not mapped by add/remove items
                    return {
                        ...order,
                    };
                }

                // case "PAYMENT_METHOD_SWITCHED": {
                //     return order;
                // }
                default:
                    assertNever(change);
            }
        },
        { ...order, _version: changeEventVersion } as OrderProjection,
    );

    return updatedOrder;
};

const orderProjectionReducer = (
    orderEvents: OrderEvent | OrderEvent[],
    initialOrderProjection?: OrderProjection,
): OrderProjection => {
    const events = asArray(orderEvents);
    const startAtVersion =
        typeof initialOrderProjection?._version === "number"
            ? initialOrderProjection._version + 1
            : 0;

    const orderId = initialOrderProjection?._id ?? events?.[0]?.orderId;
    if (!initialOrderProjection) {
        firstEventMustBeAnOrderCreatedEventCheck(orderId, events);
    }

    const sortedEvents = events
        .filter(item => item.orderId === orderId)
        .filter(item => item._version >= startAtVersion)
        .sort((a, b) => a._version - b._version);
    missingOrderEventCheck(orderId, sortedEvents, { startAtVersion });

    const result = sortedEvents.reduce(
        (projection, event: OrderEvent) => {
            if (
                !(
                    event.type === "CREATED" ||
                    event.type === "CREATED_FROM_QUOTE"
                ) &&
                event._version !== projection._version + 1
            )
                throw new VersionMismatchError(
                    `Can't apply event with version ${event._version} to order at version ${projection._version}; ${projection._id}`,
                );

            if (
                (event.type === "CREATED" ||
                    event.type === "CREATED_FROM_QUOTE") &&
                event._version !== 0
            ) {
                throw new Error(
                    `Can't apply a created event to order with a version of ${event._version}, needs to be 0; ${orderId}`,
                );
            }

            // Updates for all events
            projection.events.push(miniOrderEventFactory(event));

            // Updates for specific events
            switch (event.type) {
                case "CREATED": {
                    const output: OrderProjection = {
                        ...projection,
                        ...event.order,
                        _type: "ORDER_PROJECTION",
                        _version: event._version,
                        _versionDate: nowIsoAndUnix(),
                        createdEventId: event._id,
                        amountUnsettled:
                            event.order.totalPrice.amountIncludingVat,
                        amountUnpaid: event.order.totalPrice.amountIncludingVat,
                        amountPaid: 0,
                        amountCapturable: 0,
                        amountSettled: 0,
                        amountPendingRefund: 0,
                        amountRefunded: 0,
                        onPremiseOrder: event.onPremiseOrder,
                        orderGroupHasLegacyServiceFee:
                            event.orderGroupHasLegacyServiceFee,
                        fulfillmentDate: parseFulfillmentDate(event),
                        hasPendingOnPremisePayment: false,
                        hasPendingPayRequest: false,

                        // Auto set accommodationPurpose to LEISURE if not set and
                        // order is of type ACCOMMODATION
                        ...(event.order.type === "ACCOMMODATION"
                            ? {
                                  accommodationPurpose:
                                      event.order.accommodationPurpose ||
                                      "LEISURE",
                                  accommodationSubTypeCount:
                                      event.order.accommodationSubTypeCount,
                              }
                            : {}),
                    };
                    return output;
                }
                case "CREATED_FROM_QUOTE": {
                    const output: OrderProjection = {
                        ...projection,
                        ...event.order,
                        _type: "ORDER_PROJECTION",
                        _version: event._version,
                        _versionDate: nowIsoAndUnix(),
                        createdEventId: event._id,
                        amountUnsettled:
                            event.order.totalPrice.amountIncludingVat,
                        amountUnpaid: event.order.totalPrice.amountIncludingVat,
                        amountPaid: 0,
                        amountCapturable: 0,
                        amountSettled: 0,
                        amountPendingRefund: 0,
                        amountRefunded: 0,
                        onPremiseOrder: true,
                        orderGroupHasLegacyServiceFee: false,
                        fulfillmentDate: parseFulfillmentDate(event),
                        hasPendingOnPremisePayment: false,
                        hasPendingPayRequest: false,
                        fromQuote: event.quote,
                        // Auto set accommodationPurpose to LEISURE if not set and
                        // order is of type ACCOMMODATION
                        ...(event.order.type === "ACCOMMODATION"
                            ? {
                                  accommodationPurpose:
                                      event.order.accommodationPurpose ||
                                      "LEISURE",
                                  accommodationSubTypeCount:
                                      event.order.accommodationSubTypeCount,
                              }
                            : {}),
                    };
                    return output;
                }
                case "ORDER_CHANGED": {
                    //Order change contains grouped changed events so run the events through the reducer
                    const projectionWithChanges = applyOrderChanges(
                        projection,
                        event._version,
                        event.changes,
                    );

                    // Calculate total price of
                    // order changed event
                    const priceChange = sumPrices(
                        event.changes.map(c => c.price),
                    );

                    // If the change is negative, we'll first check if the
                    // amount unpaid is greater or equal to the change price.
                    // If it is, we can offset the price of the change against
                    // the unpaid amount of the order.
                    const changeAmount = priceChange.amountIncludingVat;
                    const unpaidAmount = projection.amountUnpaid;
                    const leftToPay = unpaidAmount + changeAmount;
                    // // eslint-disable-next-line no-console
                    // console.log("AMOUNTS: ", {
                    //     changeAmount,
                    //     unpaidAmount,
                    //     leftToPay,
                    // });

                    // Vi kan kvitta beloppet som ska återbetalas
                    // mot det obetalda beloppet om det finns något
                    if (leftToPay >= 0) {
                        return {
                            ...projectionWithChanges,
                            fulfillmentDate: parseFulfillmentDate(event),
                            amountUnpaid: leftToPay,
                            amountPendingRefund: 0,
                        };
                    }

                    // Om belopp kvar att betala kvar att betala skulle
                    // vara negativt, så har vi ett belopp som ska återbetalas
                    if (leftToPay < 0) {
                        const leftToRepay = Math.abs(leftToPay);
                        return {
                            ...projectionWithChanges,
                            fulfillmentDate: parseFulfillmentDate(event),
                            amountUnpaid: 0,
                            amountPendingRefund: leftToRepay,
                        };
                    }

                    return projectionWithChanges;
                }
                case "CONFIRMED": {
                    return {
                        ...projection,
                        _version: event._version,
                        prepTime: event.prepTime,
                        hasBeenConfirmed: true,
                        delivery: {
                            ...projection.delivery,
                            //If no new time is set with confirmed event, use the one from previous event, legacy
                            estimatedDeliveryTime:
                                event.estimatedDeliveryTime ??
                                projection.delivery.estimatedDeliveryTime,
                        },
                        fulfillmentDate:
                            projection.type === "BUYABLE"
                                ? parseFulfillmentDate(
                                      event,
                                      projection.fulfillmentDate,
                                  )
                                : projection.fulfillmentDate,
                        status: {
                            ...orderStatusConfirmedFactory({
                                ...event,
                                confirmedBy: event.confirmedBy,
                            }),
                            _createdAt: event._createdAt,
                        },
                    };
                }
                case "FULFILLED": {
                    return {
                        ...projection,
                        _version: event._version,
                        hasBeenFulfilled: true,
                        inactive: true,
                        hasPassed: true,
                        status: {
                            ...orderStatusFulfilledFactory({
                                ...event,
                            }),
                            _createdAt: event._createdAt,
                        },
                    };
                }
                case "CANCELLED": {
                    return {
                        ...projection,
                        _version: event._version,
                        hasBeenCancelled: true,
                        inactive: true,
                        status: {
                            ...orderStatusCancelledFactory({
                                ...event,
                                cancelledBy: event.cancelledBy,
                            }),
                            _createdAt: event._createdAt,
                        },
                    };
                }
                case "BOOKABLE_TABLE_RESERVATION_UPDATED":
                    //Just info event
                    return { ...projection, _version: event._version };
                case "ORDER_ITEM_BOOKABLE_TIME_UPDATED": {
                    const item = projection.orderItems.find(
                        oi => oi._id === event.orderItemId,
                    );

                    const items = projection.orderItems.map(oi => {
                        if (
                            oi._id === event.orderItemId &&
                            isOrderItemBookable(oi)
                        ) {
                            const newOrderItem: OrderItemBookable_V2 = {
                                ...oi,
                                time: event.to,
                            };
                            return newOrderItem;
                        }
                        //Also update children quantity with the same time
                        if (
                            item?._id === oi.parentId &&
                            isOrderItemBookableQuantity(oi) &&
                            oi.time?._id === event.from._id
                        ) {
                            return {
                                ...oi,
                                time: event.to,
                            };
                        }

                        return oi;
                    });
                    return {
                        ...projection,
                        _version: event._version,
                        fulfillmentDate: event.to.start,
                        orderItems: items,
                    };
                }
                case "ORDER_ITEM_PRODUCT_HAS_ACCESS": {
                    const items = projection.orderItems.map(oi => {
                        if (oi._id !== event.orderItemId) {
                            return oi;
                        }
                        if (isOrderItemBookable(oi)) {
                            const newOrderItem: OrderItemBookable_V2 = {
                                ...oi,
                                hasAccess: true,
                            };
                            return newOrderItem;
                        }
                        if (isOrderItemAccommodation(oi)) {
                            const newOrderItem: OrderItemAccommodation_V2 = {
                                ...oi,
                                hasAccess: true,
                            };
                            return newOrderItem;
                        }
                        //This is a information event, no changes to order that is not mapped by add/remove items
                        return oi;
                    });
                    return {
                        ...projection,
                        _version: event._version,
                        orderItems: items,
                    };
                }
                case "OUT_FOR_DELIVERY":
                    return {
                        ...projection,
                        _version: event._version,
                        status: {
                            ...orderStatusOutForDeliveryFactory({
                                ...event,
                            }),
                            _createdAt: event._createdAt,
                        },
                    };
                case "REJECTED":
                    return {
                        ...projection,
                        _version: event._version,
                        hasBeenRejected: true,
                        inactive: true,
                        status: {
                            ...orderStatusRejectedFactory({
                                ...event,
                            }),
                            _createdAt: event._createdAt,
                        },
                    };
                case "READY_FOR_PICK_UP":
                    return {
                        ...projection,
                        _version: event._version,
                        status: {
                            ...orderStatusReadyForPickUpFactory({
                                ...event,
                            }),
                            _createdAt: event._createdAt,
                        },
                    };
                case "PENDING_PAYMENT_CONFIRMATION":
                    return {
                        ...projection,
                        _version: event._version,
                        status: {
                            ...orderStatusPendingPaymentConfirmationFactory({
                                ...event,
                            }),
                            _createdAt: event._createdAt,
                        },
                    };
                case "PENDING_CONFIRMATION": {
                    // # LEGACY FIX FOR ORDERS MADE BEFORE AUGUST 2021
                    const createdEvent = sortedEvents.find(isOrderCreatedEvent);
                    const legacyPaid = (createdEvent &&
                        legacyOrderPaidPrice(createdEvent, events)) || {
                        isLegacy: false,
                        price: undefined,
                    };

                    return {
                        ...projection,
                        _version: event._version,
                        delivery: {
                            ...projection.delivery,
                            estimatedDeliveryTime: event.estimatedDeliveryTime,
                        },
                        status: {
                            ...orderStatusPendingConfirmationFactory({
                                ...event,
                            }),
                            _createdAt: event._createdAt,
                        },
                        // If the order has been paid without a payment fulfilled event,
                        // legacy status before summer of 2021 then
                        ...(legacyPaid.isLegacy
                            ? {
                                  amountPaid:
                                      projection.amountPaid +
                                      legacyPaid.price.amountIncludingVat,
                                  amountUnpaid:
                                      projection.amountUnpaid -
                                      legacyPaid.price.amountIncludingVat,
                                  amountSettled:
                                      projection.amountSettled +
                                      legacyPaid.price.amountIncludingVat,
                                  amountUnsettled:
                                      projection.amountUnsettled -
                                      legacyPaid.price.amountIncludingVat,
                              }
                            : {}),
                    };
                }
                case "PAYMENT_REFUND_FAILED": {
                    return {
                        ...projection,
                        _version: event._version,
                        amountUnsettled:
                            projection.amountUnsettled +
                            event.requestedRefundAmount,
                        amountPendingRefund:
                            projection.amountPendingRefund -
                            event.requestedRefundAmount,
                    };
                }
                case "ORDER_CHANGE_REFUND_REQUESTED": {
                    return {
                        ...projection,
                        _version: event._version,
                        amountUnsettled:
                            projection.amountUnsettled -
                            event.amount.amountIncludingVat,
                        amountPendingRefund:
                            projection.amountPendingRefund +
                            event.amount.amountIncludingVat,
                    };
                }
                case "PARTIAL_REFUND_REQUESTED": {
                    return {
                        ...projection,
                        _version: event._version,
                        amountUnsettled:
                            projection.amountUnsettled -
                            event.amount.amountIncludingVat,
                        amountPendingRefund:
                            projection.amountPendingRefund +
                            event.amount.amountIncludingVat,
                    };
                }
                case "FULL_REFUND_REQUESTED": {
                    return {
                        ...projection,
                        _version: event._version,
                        amountUnsettled:
                            projection.amountUnsettled -
                            projection.amountSettled,
                        amountPendingRefund:
                            projection.amountPendingRefund +
                            projection.amountSettled,
                    };
                }
                case "PAYMENT_REFUND_PENDING":
                case "PENDING_DELIVERY":
                case "ORDER_ITEM_STATUS_SET":
                case "PAYMENT_FAILED":
                    return {
                        ...projection,
                        _version: event._version,
                    };
                case "PAY_REQUEST_EXPIRED":
                    return {
                        ...projection,
                        _version: event._version,
                        hasPendingPayRequest: false,
                    };
                case "PAY_REQUEST_PENDING":
                    return {
                        ...projection,
                        _version: event._version,
                        hasPendingPayRequest: true,
                    };
                case "PAYMENT_REFUND_INITIATED":
                    return { ...projection, _version: event._version };
                case "PENDING_PAYMENT_ONPREMISE":
                    return {
                        ...projection,
                        _version: event._version,
                        hasPendingOnPremisePayment: true,
                    };
                case "PAYMENT_REFUND_FULFILLED":
                    return {
                        ...projection,
                        _version: event._version,
                        amountRefunded:
                            projection.amountRefunded +
                            event.refundedAmount.amountIncludingVat,
                        amountPendingRefund:
                            projection.amountPendingRefund -
                            event.refundedAmount.amountIncludingVat,
                        amountSettled:
                            projection.amountSettled -
                            event.refundedAmount.amountIncludingVat,
                        amountUnsettled:
                            projection.amountUnsettled +
                            event.refundedAmount.amountIncludingVat,
                    };
                case "PAYMENT_AMOUNT_CAPTURABLE_UPDATED":
                    return {
                        ...projection,
                        _version: event._version,
                        amountCapturable:
                            projection.amountCapturable +
                            event.amountCapturable,
                        amountUnpaid:
                            projection.amountUnpaid - event.amountCapturable,
                        amountUnsettled:
                            projection.amountUnsettled - event.amountCapturable,
                    };
                case "PAYMENT_FULFILLED": {
                    // Subtract charged amount on amount capturable on payment fulfilled if there
                    // has been an amount capturable event for the same order event
                    const hasHadAnAmountCapturableEvent = sortedEvents.some(
                        eventToCheck =>
                            eventToCheck.type ===
                                "PAYMENT_AMOUNT_CAPTURABLE_UPDATED" &&
                            eventToCheck.paymentFor.orderEventId ===
                                event.paymentFor.orderEventId,
                    );

                    const eventPaidAmount =
                        typeof event.paidAmount === "number"
                            ? event.paidAmount
                            : event.paidAmount.amountIncludingVat;

                    return {
                        ...projection,
                        _version: event._version,
                        amountPaid: projection.amountPaid + eventPaidAmount,
                        amountUnpaid: hasHadAnAmountCapturableEvent
                            ? projection.amountUnpaid
                            : projection.amountUnpaid - eventPaidAmount,
                        amountSettled:
                            projection.amountSettled + eventPaidAmount,
                        amountUnsettled: hasHadAnAmountCapturableEvent
                            ? projection.amountUnsettled
                            : projection.amountUnsettled - eventPaidAmount,
                        amountCapturable: hasHadAnAmountCapturableEvent
                            ? projection.amountCapturable - eventPaidAmount
                            : projection.amountCapturable,
                        hasPendingPayRequest: false,
                        hasPendingOnPremisePayment: false,
                    };
                }
                case "PAY_REQUEST_FULFILLED": {
                    // Subtract charged amount on amount capturable on payment fulfilled if there
                    // has been an amount capturable event for the same order event
                    const hasHadAnAmountCapturableEvent = sortedEvents.some(
                        eventToCheck =>
                            eventToCheck.type ===
                                "PAYMENT_AMOUNT_CAPTURABLE_UPDATED" &&
                            event.paymentFor.orderEventIds.includes(
                                eventToCheck.paymentFor.orderEventId,
                            ),
                    );

                    const eventPaidAmount =
                        typeof event.price === "number"
                            ? event.price
                            : event.price.amountIncludingVat;

                    return {
                        ...projection,
                        _version: event._version,
                        amountPaid: projection.amountPaid + eventPaidAmount,
                        amountUnpaid: hasHadAnAmountCapturableEvent
                            ? projection.amountUnpaid
                            : projection.amountUnpaid - eventPaidAmount,
                        amountSettled:
                            projection.amountSettled + eventPaidAmount,
                        amountUnsettled: hasHadAnAmountCapturableEvent
                            ? projection.amountUnsettled
                            : projection.amountUnsettled - eventPaidAmount,
                        amountCapturable: hasHadAnAmountCapturableEvent
                            ? projection.amountCapturable - eventPaidAmount
                            : projection.amountCapturable,
                        hasPendingPayRequest: false,
                    };
                }
                case "PAY_REQUEST_CANCELLED": {
                    return {
                        ...projection,
                        hasPendingPayRequest: false,
                        _version: event._version,
                    };
                }
                case "ON_PREMISE_PAY_LATER_CANCELLED": {
                    return {
                        ...projection,
                        hasPendingOnPremisePayment: false,
                        _version: event._version,
                    };
                }
                case "PAYMENT_AMOUNT_RELEASED": {
                    return {
                        ...projection,
                        _version: event._version,
                        amountCapturable:
                            projection.amountCapturable - event.amountReleased,
                    };
                }
                case "CUSTOMER_ANONYMIZED": {
                    return {
                        ...projection,
                        _version: event._version,
                        contactInfo: anonymizedContactInfoFactory(
                            projection.contactInfo,
                        ),
                    } as OrderProjection;
                }
                case "PAYMENT_METHOD_SWITCHED": {
                    return {
                        ...projection,
                        _version: event._version,
                    };
                }
                case "CONTACT_INFO_CHANGED": {
                    return {
                        ...projection,
                        _version: event._version,
                        contactInfo: event.changedTo,
                    };
                }
                case "ORDER_CONFIRMATION_MAIL_SENT":
                    return {
                        ...projection,
                        _version: event._version,
                    };
                case "ORDER_IMPORTED":
                    return {
                        ...projection,
                        channel: {
                            bookingId: event.channel.bookingId,
                            otaName: event.channel.name,
                            paymentWithVirtualCard:
                                projection.channel?.paymentWithVirtualCard ??
                                false,
                            uniqueId: event.channel.uniqueId,
                        },
                        _version: event._version,
                    };
                case "PAYMENT_AMOUNT_PREPAID":
                    return {
                        ...projection,
                        _version: event._version,
                        amountUnsettled:
                            projection.amountUnsettled - event.amount,
                        amountUnpaid: projection.amountUnpaid - event.amount,
                    };
                case "PAYMENT_CARD_GUARANTEE":
                    return {
                        ...projection,
                        _version: event._version,
                    };
                case "PAYMENT_VIRTUAL_CARD_REGISTERED": {
                    if (projection.channel) {
                        return {
                            ...projection,
                            _version: event._version,
                            channel: {
                                ...projection.channel,
                                paymentWithVirtualCard: true,
                            },
                        };
                    }
                    return {
                        ...projection,
                        _version: event._version,
                        channel: {
                            bookingId: "",
                            otaName: "",
                            uniqueId: "",
                            paymentWithVirtualCard: true,
                        },
                    };
                }

                default:
                    assertNever(event);
            }
        },
        {
            _versionDate: nowIsoAndUnix(),
            events: [],
            amountCapturable: 0,
            amountSettled: 0,
            amountPendingRefund: 0,
            amountRefunded: 0,
            amountUnsettled: 0,
            ...initialOrderProjection,
            hasBeenCancelled: !!initialOrderProjection?.hasBeenCancelled,
            hasBeenConfirmed: !!initialOrderProjection?.hasBeenConfirmed,
            hasBeenFulfilled: !!initialOrderProjection?.hasBeenFulfilled,
            hasBeenRejected: !!initialOrderProjection?.hasBeenRejected,
            hasPassed: !!initialOrderProjection?.hasPassed,
        } as OrderProjection,
    );

    // Check if order has passed,
    // don't know if we should keep this forever
    // there could be different parts of an order that get's "passed"
    // at different times
    const bookableOrderItems =
        result.orderItems.filter(isOrderItemBookable) || [];

    const hasPassed =
        result.hasPassed ||
        (!!bookableOrderItems.length &&
            bookableOrderItems.every(item => isBeforeNow(item.time.end)));

    const inactive =
        hasPassed ||
        result.inactive ||
        result.hasBeenCancelled ||
        result.hasBeenRejected ||
        result.hasBeenFulfilled;

    return {
        ...result,
        hasPassed,
        inactive,
    };
};

export { applyOrderChanges, checkOrderId, orderProjectionReducer };
